import i18next from 'i18next';
import {
  DeviceTypeWithoutGatewayDataConfigParameterModel,
  DeviceTypeWithoutGatewayDataExtraInformationModel,
  DeviceTypeWithoutGatewayDataModel,
  DeviceTypeWithoutGatewayDataSensorModel,
  DeviceWithoutGatewayExtraInformationModel,
  DeviceWithoutGatewayModel,
  DeviceWithoutGatewayParameterModel,
  DeviceWithoutGatewaySensorModel,
} from 'core/domain/devices/models';
import { getInitialFormValues } from '../utils';
import {
  DEVICE_WITHOUT_GATEWAY_FORM_SENSOR_ROOM_KEY,
  DeviceWithoutGatewayConfigParametersFormValuesNames,
  DeviceWithoutGatewayExtraInformationFormValuesNames,
  DeviceWithoutGatewayFormDataModel,
  DeviceWithoutGatewayGeneralFormValuesNames,
  DeviceWithoutGatewayGeneralFormValuesSetModel,
  DeviceWithoutGatewaySensorFormValuesNames,
  ONLY_NUMERIC_VALUES_REGEX,
} from '../models';

export const getFormValueRules = ({ required, valueType }: { required: boolean; valueType: string }) => {
  const rules = [];
  required && rules.push({ required, message: i18next.t('required_field') });
  valueType === 'number' &&
    rules.push({ pattern: ONLY_NUMERIC_VALUES_REGEX, message: i18next.t('_DEVICE_WITHOUT_GATEWAY_FORM_RULE_NUMERIC_VALUE') });

  return rules;
};

const setBasicFormValue = (formValue: DeviceWithoutGatewayFormDataModel, currentValue?: string): DeviceWithoutGatewayFormDataModel => {
  const formGroupValue = !!currentValue ? formValue.formGroupValue - 1 : formValue.formGroupValue;

  return { ...formValue, value: currentValue || '', formGroupValue };
};

const setFormValuesManager = {
  [DeviceWithoutGatewayGeneralFormValuesNames.NAME]: ({ formValue, data }: DeviceWithoutGatewayGeneralFormValuesSetModel) =>
    setBasicFormValue(formValue, data?.name),
  [DeviceWithoutGatewayGeneralFormValuesNames.ROOM]: ({ formValue, data }: DeviceWithoutGatewayGeneralFormValuesSetModel) =>
    setBasicFormValue(formValue, data?.room),
  [DeviceWithoutGatewayGeneralFormValuesNames.SERIAL_NUMBER]: ({ formValue, data }: DeviceWithoutGatewayGeneralFormValuesSetModel) =>
    setBasicFormValue(formValue, data?.serialNumber),
};

const getBasicFormValues = ({
  formValues,
  data,
}: {
  formValues: DeviceWithoutGatewayFormDataModel[];
  data?: DeviceWithoutGatewayModel;
}) => {
  const basicFormValuesObjectValues = Object.values(DeviceWithoutGatewayGeneralFormValuesNames);
  return formValues
    .filter((formValue) => basicFormValuesObjectValues.includes(formValue.key as DeviceWithoutGatewayGeneralFormValuesNames))
    .map((formValue) => setFormValuesManager[formValue.key as DeviceWithoutGatewayGeneralFormValuesNames]({ formValue, data }));
};

const getConfigParameterFormValues = (
  formValues: DeviceWithoutGatewayFormDataModel[],
  configParameters: DeviceTypeWithoutGatewayDataConfigParameterModel[],
  data?: DeviceWithoutGatewayModel
): DeviceWithoutGatewayFormDataModel[] => {
  return formValues.reduce<DeviceWithoutGatewayFormDataModel[]>((acc, formValue) => {
    const dataValues = data?.configParameters?.find((dataValue) => dataValue.name === formValue.key);
    const value: string = dataValues ? String(dataValues.data[0].value) : '';
    const units: string = dataValues ? String(dataValues.data[0].unit) : '';
    const formGroupValue = data ? formValue.formGroupValue - 1 : formValue.formGroupValue;

    const configParameterValues = configParameters.find((configParameter) => configParameter.name === formValue.key);

    if (!configParameterValues) return acc;

    const required = configParameterValues.required || false;
    const multiple = configParameterValues.multiple || false;
    const rules = getFormValueRules({
      required: configParameterValues.required,
      valueType: configParameterValues.valueType,
    });

    acc.push({
      ...formValue,
      value,
      units,
      required,
      multiple,
      rules,
      formGroupValue,
    });

    return acc;
  }, []);
};

const getExtraInformationFormValues = (
  formValues: DeviceWithoutGatewayFormDataModel[],
  extraInformation: DeviceTypeWithoutGatewayDataExtraInformationModel[],
  data?: DeviceWithoutGatewayModel
): DeviceWithoutGatewayFormDataModel[] => {
  return formValues.reduce<DeviceWithoutGatewayFormDataModel[]>((acc, formValue) => {
    const dataValues = data?.extraInformation?.find((dataValue) => dataValue.name === formValue.key);
    const value = dataValues ? String(dataValues.value) : '';
    const formGroupValue = data ? formValue.formGroupValue - 1 : formValue.formGroupValue;

    const extraInformationValues = extraInformation.find((info) => info.name === formValue.key);

    if (!extraInformationValues) return acc;

    const required = extraInformationValues.required || false;
    const rules = getFormValueRules({
      required: extraInformationValues.required,
      valueType: extraInformationValues.valueType,
    });

    acc.push({
      ...formValue,
      value,
      required,
      rules,
      formGroupValue,
    });

    return acc;
  }, []);
};

const getSensorsFormValues = (
  formValues: DeviceWithoutGatewayFormDataModel[],
  sensors: DeviceTypeWithoutGatewayDataSensorModel[],
  data?: DeviceWithoutGatewayModel
): DeviceWithoutGatewayFormDataModel[] => {
  const combinedSensorsFormValues: DeviceWithoutGatewayFormDataModel[] = [];
  formValues.forEach((formValue) => {
    const dataValues = data?.sensors?.find((dataValue) => formValue.key === dataValue.label);
    const uuid = dataValues?.sensorUuid || '';
    const id = dataValues?.sensorId || '';
    const formGroupValue = !!data ? formValue.formGroupValue - 1 : formValue.formGroupValue;
    const sensor = sensors.find((sensor) => formValue.key === sensor.label);
    const type = sensor?.sensorType || '';

    const selectedNameSensor = {
      ...formValue,
      value: dataValues?.name || '',
      id,
      uuid,
      type,
      label: i18next.t('_DEVICE_WITHOUT_GATEWAY_FORM_SENSOR_LABEL_NAME'),
      placeholder: i18next.t('_DEVICE_WITHOUT_GATEWAY_FORM_SENSOR_PLACEHOLDER_NAME'),
      formGroupValue,
      relatedFormKey: `${formValue.key}_room`,
    };

    const selectedRoomSensor = {
      ...formValue,
      key: `${formValue.key}_room`,
      value: dataValues?.room || '',
      title: '',
      label: i18next.t('_DEVICE_WITHOUT_GATEWAY_FORM_SENSOR_LABEL_ROOM'),
      placeholder: i18next.t('_DEVICE_WITHOUT_GATEWAY_FORM_SENSOR_PLACEHOLDER_ROOM'),
      id,
      uuid,
      type,
      formGroupValue,
    };

    !!sensor && combinedSensorsFormValues.push(selectedNameSensor, selectedRoomSensor);
  });

  return combinedSensorsFormValues;
};

export const setDeviceWithoutGatewayFormValues = ({
  deviceType,
  data,
}: {
  deviceType: DeviceTypeWithoutGatewayDataModel;
  data?: DeviceWithoutGatewayModel;
}): DeviceWithoutGatewayFormDataModel[] => {
  const formValues = getInitialFormValues();

  const { configParameters, extraInformation, sensors } = deviceType;
  const basicFormValues = getBasicFormValues({ formValues, data });
  const configParametersFormValues = getConfigParameterFormValues(formValues, configParameters, data);
  const extraInformationFormValues = getExtraInformationFormValues(formValues, extraInformation, data);
  const sensorsFormValues = getSensorsFormValues(formValues, sensors, data);

  return [...basicFormValues, ...extraInformationFormValues, ...configParametersFormValues, ...sensorsFormValues];
};

const configParametersToPayload = ({
  values,
  formValues,
}: {
  values: Record<string, string | null>;
  formValues: DeviceWithoutGatewayFormDataModel[];
}): DeviceWithoutGatewayParameterModel[] => {
  const enumConfigParametersValues = Object.values(DeviceWithoutGatewayConfigParametersFormValuesNames);

  return Object.keys(values)
    .filter((value) => enumConfigParametersValues.includes(value as DeviceWithoutGatewayConfigParametersFormValuesNames))
    .map((key) => {
      const formValue = formValues.find((formValue) => formValue.key === key);

      return {
        name: formValue?.key || '',
        data: [{ value: values[key] || null, unit: formValue?.units || null }],
        multiple: formValue?.multiple || false,
        required: formValue?.required || false,
      };
    });
};

const extraInformationToPayload = ({
  values,
  formValues,
}: {
  values: Record<string, string | null>;
  formValues: DeviceWithoutGatewayFormDataModel[];
}): DeviceWithoutGatewayExtraInformationModel[] => {
  const enumExtraInformationValues = Object.values(DeviceWithoutGatewayExtraInformationFormValuesNames);

  return Object.keys(values)
    .filter((value) => enumExtraInformationValues.includes(value as DeviceWithoutGatewayExtraInformationFormValuesNames))
    .map((key) => {
      const formValue = formValues.find((formValue) => formValue.key === key);

      return {
        name: formValue?.key || '',
        value: values[key] ?? null,
        required: formValue?.required || false,
      };
    });
};

const sensorsToPayload = ({
  values,
  formValues,
}: {
  values: Record<string, string | null>;
  formValues: DeviceWithoutGatewayFormDataModel[];
}): DeviceWithoutGatewaySensorModel[] => {
  const enumSensorValues = Object.values(DeviceWithoutGatewaySensorFormValuesNames);

  return Object.keys(values)
    .filter((value) => enumSensorValues.includes(value as DeviceWithoutGatewaySensorFormValuesNames))
    .filter((value) => !value.includes(DEVICE_WITHOUT_GATEWAY_FORM_SENSOR_ROOM_KEY))
    .map((key) => {
      const formValue = formValues.find((formValue) => formValue.key === key);
      const room = !!formValue && !!formValue.relatedFormKey ? values[formValue.relatedFormKey] : null;

      return {
        label: formValue?.key || '',
        sensorType: formValue?.type || '',
        sensorId: formValue?.id || '',
        sensorUuid: formValue?.uuid || '',
        name: values[key] || null,
        room: room || null,
      };
    });
};

export const formValuesToPayload = ({
  assetId,
  projectId,
  formType,
  values,
  formValues,
  data,
}: {
  assetId: string | null;
  projectId: string | null;
  formType: string | null;
  values: Record<string, string | null>;
  formValues: DeviceWithoutGatewayFormDataModel[];
  data?: DeviceWithoutGatewayModel;
}): DeviceWithoutGatewayModel => {
  const name = values[DeviceWithoutGatewayGeneralFormValuesNames.NAME];
  const room = values[DeviceWithoutGatewayGeneralFormValuesNames.ROOM];
  const serialNumber = values[DeviceWithoutGatewayGeneralFormValuesNames.SERIAL_NUMBER];

  const deviceUuid = !!data ? data.deviceUuid : '';

  const configParameters = configParametersToPayload({ values, formValues });
  const extraInformation = extraInformationToPayload({ values, formValues });
  const sensors = sensorsToPayload({ values, formValues });

  return {
    assetId,
    projectId,
    deviceType: formType || '',
    deviceUuid,
    name: name ?? '',
    room: room ?? '',
    serialNumber: serialNumber ?? '',
    configParameters,
    sensors,
    extraInformation,
  };
};
